import { NgClass, NgStyle } from '@angular/common';
import { Component, computed, input } from '@angular/core';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { v4 as uuidv4 } from 'uuid';
import { ISkeletonColumn, ISkeletonLoaderFilter } from './skeleton-loader.interface';

@Component({
  imports: [NgxSkeletonLoaderModule, NgClass, NgStyle],
  selector: 'app-skeleton-loader',
  templateUrl: './skeleton-loader.component.html',
})
export class SkeletonLoaderComponent {
  public readonly containerClass = input<string | undefined>('skeleton-container');
  public readonly showHeaderControls = input<boolean>(false);
  public readonly showSearchField = input<boolean>(false);
  public readonly showCreateButton = input<boolean>(false);
  public readonly showFilters = input<boolean>(false);
  public readonly filters = input<ISkeletonLoaderFilter[]>([]);
  public readonly filterContainerClass = input<string | undefined>('flex items-center gap-2');
  public readonly showColumns = input<boolean>(true);
  public readonly columnGap = input<string>('0');
  public readonly rows = input<number>(5);
  public readonly columns = input<ISkeletonColumn[]>([]);
  public readonly headerPadding = input<string>('0 16px');
  public readonly headerMargin = input<string>('0');
  public readonly showPaginator = input<boolean>(true);
  public readonly hasRowLines = input<boolean>(true);

  public readonly defaultRowValueHeight = '20px' as const;
  public readonly defaultRowPadding = '12px 0' as const;
  public readonly defaultRowMargin = '0' as const;
  public readonly defaultColumnPadding = '0 16px' as const;
  // An inset shadow instead of a border, so the line doesn't add 1px to rows sized to match the table.
  public readonly rowLineShadow = 'inset 0 -1px 0 var(--shikenso-gray-300)' as const;

  public readonly searchInputTheme = { 'border-radius': '100vw', height: '48px', width: '100%' };
  public readonly filterInputTheme = { 'border-radius': '100vw', height: '48px' };
  public readonly createButtonTheme = { 'border-radius': '4px', height: '44px', width: '140px' };
  public readonly headerColumnTheme = { 'border-radius': '4px', height: '20px', width: '60px' };

  public readonly iconTheme = { 'border-radius': '100vw', height: this.defaultRowValueHeight, width: '32px' };
  public readonly textWithIconTheme = { 'border-radius': '4px', height: this.defaultRowValueHeight, width: '100px' };
  public readonly iconOnlyTheme = { 'border-radius': '50%', height: this.defaultRowValueHeight, width: '32px' };
  public readonly textTheme = { 'border-radius': '4px', height: '20px', width: '100%' };
  public readonly paginatorShortTheme = { height: '24px', width: '192px' };
  public readonly paginatorLongTheme = { height: '24px', width: '192px' };

  public readonly columnsFormatted = computed(() => this.columns().map((column) => ({ ...column, id: uuidv4() })));

  public get rowsArray(): number[] {
    return Array(this.rows())
      .fill(0)
      .map((_, i) => i);
  }

  public getColumnClass(column: ISkeletonColumn): string {
    if (column.className) return column.className;

    return column.width ? '' : 'flex-1';
  }

  public getCol(column: ISkeletonColumn, i: number): ISkeletonColumn {
    if (column && column.perRow && column.perRow[i]) {
      return { ...column, ...column.perRow[i] };
    }

    return column;
  }
}
